import React, { useEffect, useState, useRef } from 'react';
import { FadeIn } from './FadeIn';
import { SpendingCycleDiagram } from './SpendingCycleDiagram';
import { PullQuote } from './PullQuote';
function AnimatedFlow() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, {
      threshold: 0.5
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  const steps = ['Stress', 'Scroll', 'Purchase', 'Relief'];
  return <div ref={ref} className="flex items-center justify-center gap-1 md:gap-2 my-3 md:my-5" data-id="element-398">
      {steps.map((step, i) => <div key={step} className="flex items-center gap-1 md:gap-2" data-id="element-399">
          <span className={`text-gold font-medium tracking-wider text-xs md:text-base px-2 md:px-3 py-1 md:py-1.5 rounded-full border border-gold/30 bg-gold/10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-2 scale-95'}`} style={{
        transitionDelay: `${i * 250}ms`
      }} data-id="element-400">
            {step}
          </span>
          {i < steps.length - 1 && <span className={`text-gold/50 text-xs transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`} style={{
        transitionDelay: `${i * 250 + 150}ms`
      }} data-id="element-401">
              →
            </span>}
        </div>)}
    </div>;
}
function ScrollReward() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [loopCount, setLoopCount] = useState(0);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setVisible(true);
        obs.disconnect();
      }
    }, {
      threshold: 0.5
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => {
      setLoopCount(c => c + 1);
    }, 2200);
    return () => clearInterval(interval);
  }, [visible]);
  return <div ref={ref} className="my-3 md:my-5 text-center" data-id="element-402">
      <div className="inline-flex flex-col gap-1" data-id="element-403">
        {[0, 1].map(row => <div key={row} className="flex items-center justify-center gap-2 md:gap-3" data-id="element-404">
            <span className={`font-serif text-xl md:text-2xl transition-all duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`} style={{
          transitionDelay: `${row * 400}ms`,
          color: (loopCount + row) % 2 === 0 ? '#C8A96E' : 'rgba(255,255,255,0.4)'
        }} data-id="element-405">
              Scroll.
            </span>
            <span className={`font-serif text-xl md:text-2xl font-bold transition-all duration-700 ${visible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'}`} style={{
          transitionDelay: `${row * 400 + 200}ms`,
          color: (loopCount + row) % 2 === 0 ? 'rgba(255,255,255,0.9)' : '#C8A96E',
          textShadow: (loopCount + row) % 2 !== 0 ? '0 0 20px rgba(200,169,110,0.4)' : 'none'
        }} data-id="element-406">
              Reward.
            </span>
          </div>)}
      </div>
      <p className="text-cream/30 text-[10px] md:text-xs mt-2 italic tracking-wider" data-id="element-407">
        ↻ repeat
      </p>
    </div>;
}
export function TruthSection() {
  return <section className="py-4 md:py-12 px-4 md:px-6 bg-navy text-cream" data-id="element-408">
      <div className="max-w-[640px] mx-auto space-y-1.5 md:space-y-3 text-sm md:text-base font-light" data-id="element-409">
        <FadeIn data-id="element-410">
          <h2 className="font-serif text-xl md:text-2xl text-gold mb-3 md:mb-6 text-center leading-tight" data-id="element-411">
            The Truth Most Financial Advice Gets Wrong
          </h2>
        </FadeIn>

        <FadeIn data-id="element-412">
          <p data-id="element-413">
            Most financial advice assumes overspending happens because of bad
            discipline.
          </p>
          <p className="mt-1.5" data-id="element-414">So the solutions always sound the same:</p>
        </FadeIn>

        <FadeIn data-id="element-415">
          <div className="mt-2 space-y-1.5 pl-3 border-l-2 border-red-400/50" data-id="element-416">
            <p className="italic text-cream/50 line-through decoration-red-400/70 decoration-2" data-id="element-417">
              Track every dollar
            </p>
            <p className="italic text-cream/50 line-through decoration-red-400/70 decoration-2" data-id="element-418">
              Create a strict budget
            </p>
            <p className="italic text-cream/50 line-through decoration-red-400/70 decoration-2" data-id="element-419">
              Stop buying unnecessary things
            </p>
          </div>
        </FadeIn>

        <FadeIn data-id="element-420">
          <p className="mt-3 md:mt-6" data-id="element-421">
            But if discipline were the real problem…
          </p>
          <p className="mt-1" data-id="element-422">you would have already solved this.</p>
          <p className="mt-2" data-id="element-423">Because the real issue isn't discipline.</p>
        </FadeIn>

        <PullQuote text="It's conditioning." className="border-gold/30" textClassName="text-gold" data-id="element-424" />

        <FadeIn data-id="element-425">
          <p data-id="element-426">
            Your brain has quietly learned to associate shopping with emotional
            relief.
          </p>
          <AnimatedFlow data-id="element-427" />
          <p data-id="element-428">Over time this becomes automatic. A subconscious loop.</p>
          <p className="mt-1.5" data-id="element-429">
            Not because you're careless. But because your brain believes
            shopping helps you feel better.
          </p>
        </FadeIn>

        <FadeIn data-id="element-430">
          <h3 className="font-serif text-lg md:text-xl text-gold mt-4 md:mt-8 mb-3 text-center" data-id="element-431">
            The Emotional Spending Program
          </h3>
          <p data-id="element-432">Most emotional spenders are unknowingly running what we call:</p>
          <p className="font-serif text-sm md:text-base text-gold italic mt-1.5" data-id="element-433">
            The Emotional Spending Program
          </p>
        </FadeIn>
      </div>

      <div className="max-w-5xl mx-auto px-4" data-id="element-434">
        <SpendingCycleDiagram data-id="element-435" />
      </div>

      <div className="max-w-[640px] mx-auto space-y-1.5 md:space-y-3 text-sm md:text-base font-light mt-4 md:mt-8" data-id="element-436">
        <FadeIn data-id="element-437">
          <p data-id="element-438">Then the cycle repeats.</p>
          <p className="mt-1.5" data-id="element-439">
            But here's the part most people never realize.
          </p>
          <p className="mt-1.5" data-id="element-440">This program didn't appear randomly.</p>
          <p className="font-serif text-base md:text-lg text-gold mt-2 font-medium" data-id="element-441">
            It was installed.
          </p>
        </FadeIn>

        <FadeIn data-id="element-442">
          <h3 className="font-serif text-lg md:text-xl text-gold mt-4 md:mt-8 mb-3 text-center" data-id="element-443">
            The Forces Behind Your Spending
          </h3>
          <p data-id="element-444">Modern companies spend billions studying consumer psychology.</p>
          <p className="mt-1.5" data-id="element-445">
            They design apps, ads, and online stores to trigger dopamine in your
            brain.
          </p>
        </FadeIn>

        <ScrollReward data-id="element-446" />

        <FadeIn data-id="element-447">
          <p className="text-center italic text-cream/50 text-xs md:text-sm" data-id="element-448">
            The same psychological loop used in slot machines.
          </p>
        </FadeIn>

        <FadeIn data-id="element-449">
          <p className="mt-3 md:mt-6" data-id="element-450">
            Your brain isn't weak. It's responding exactly the way it was
            trained.
          </p>
          <p className="mt-2" data-id="element-451">
            But once you understand this… something powerful happens.
          </p>
        </FadeIn>

        <FadeIn data-id="element-452">
          <p className="mt-2 font-medium text-cream" data-id="element-453">
            You stop blaming yourself.
          </p>
          <p className="mt-1" data-id="element-454">And you start asking a better question.</p>
        </FadeIn>

        <FadeIn data-id="element-455">
          <p className="font-serif text-lg md:text-xl text-gold my-4 md:my-6 text-center italic border-y border-gold/30 py-3" data-id="element-456">
            How do I break the program?
          </p>
        </FadeIn>
      </div>
    </section>;
}